import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { BackArrowIcon } from "vega-icons";

import { useStore } from "@nanostores/react";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import {
  getGetDealShortlistQueryKey,
  useCreateOrAddDealShortlist,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { PageContainer } from "@vega-core/components/Layout/PageContainer";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";

import { useDealDropdownValues } from "../components/CoInvestmentEditForm/hooks/useDealDropdownValues";
import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { REASON_FOR_INCLUSION_SEPARATOR } from "../constants/prospectConstants";
import { SHARE_TAB_PARAM, ShareTabType } from "../constants/sharePageConstants";
import { useAddNotesForCommitteeColumns } from "../hooks/useAddNotesForCommitteeColumns";
import { useClosedDealRedirect } from "../hooks/useClosedDealRedirect";
import { useDealCurrency } from "../hooks/useDealCurrency";
import { useDealName } from "../hooks/useDealName";
import {
  $selectedProspectsData,
  type ProspectWithFormData,
  type ValidationErrors,
} from "../stores/prospectsStore";

export const AddNotesForCommitteePage = () => {
  useFeatureFlagGuard(
    "gp_co_investment",
    // Only redirect to home when not in GP co-invest portal
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );
  const { id: dealId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const { featureFlag } = useFeatureFlag();
  const isNewNavEnabled =
    featureFlag[FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769];

  const { currency, isLoading: isDealLoading } = useDealCurrency(dealId);
  const { inclusionReasonsOptions, isLoading: isDropdownValuesLoading } =
    useDealDropdownValues();
  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);
  const { isResolving, redirect } = useClosedDealRedirect(dealId);

  const selectedProspectsData = useStore($selectedProspectsData);
  const selectedProspects = Object.values(selectedProspectsData);

  // Use ref for validation errors to avoid recreating columns on every change
  const validationErrorsRef = useRef<Record<string, ValidationErrors>>({});

  // Ref for the table wrapper
  const tableRef = useRef<HTMLDivElement>(null);

  // Track if form has been submitted to show validation errors
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Compute validation errors for ticket size (only after submit attempt)
  const ticketSizeErrors = useMemo(() => {
    if (!hasAttemptedSubmit) return {};

    const errors: Record<string, ValidationErrors> = {};
    selectedProspects.forEach((prospect) => {
      const min = prospect.estimatedTicketSizeMin?.amount;
      const max = prospect.estimatedTicketSizeMax?.amount;

      if (min !== undefined && max !== undefined && min > max) {
        errors[prospect.limitedPartnerId] = {
          estimatedTicketSizeMin: m.formValidation_estimatedTicketSizeMin_max(),
          estimatedTicketSizeMax: m.formValidation_estimatedTicketSizeMax_min(),
        };
      }
    });

    return errors;
  }, [selectedProspects, hasAttemptedSubmit]);

  validationErrorsRef.current = ticketSizeErrors;

  const getValidationErrors = useCallback(
    () => validationErrorsRef.current,
    [],
  );

  const columns = useAddNotesForCommitteeColumns(
    currency,
    getValidationErrors,
    tableRef,
    inclusionReasonsOptions,
  );

  // Blur focused inputs when scrolling horizontally
  useEffect(() => {
    const scrollableWrapper = tableRef.current;

    if (!scrollableWrapper) return;

    let lastScrollLeft = scrollableWrapper.scrollLeft;

    const handleScroll = () => {
      const currentScrollLeft = scrollableWrapper.scrollLeft;

      // Only blur if horizontal scroll occurred
      if (currentScrollLeft !== lastScrollLeft) {
        const activeElement = document.activeElement;

        // Check if the active element is an input inside the table
        if (
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement ||
          activeElement instanceof HTMLButtonElement
        ) {
          const isInsideTable = scrollableWrapper.contains(activeElement);

          if (isInsideTable) {
            activeElement.blur();
          }
        }

        lastScrollLeft = currentScrollLeft;
      }
    };

    scrollableWrapper.addEventListener("scroll", handleScroll);

    return () => {
      scrollableWrapper.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const mutation = useCreateOrAddDealShortlist({
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.status_success(),
          description: m.coInvestments_addedToShortlist(),
          variant: "success",
        });
        $selectedProspectsData.set({});
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
        const destination = isNewNavEnabled
          ? CoInvestmentFullPaths.CoInvestmentShare.replace(
              ":id",
              dealId,
            ).concat(
              `?${new URLSearchParams({ [SHARE_TAB_PARAM]: ShareTabType.Pending })}`,
            )
          : CoInvestmentFullPaths.CoInvestmentProspects.replace(":id", dealId);

        navigate(destination);
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_addToShortlist(),
        });
      },
    },
  });

  if (!dealId) {
    return <Navigate replace to={CommonPaths.Home} />;
  }

  // Closed deals can't add prospects: block direct access to this route
  if (isResolving) {
    return (
      <PageContainer className="bg-background/subtle py-12">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (redirect) return redirect;

  const backLink =
    CoInvestmentFullPaths.CoInvestmentProspectsFindFromSalesforce.replace(
      ":id",
      dealId,
    );

  if (isDealLoading || isDropdownValuesLoading || isDealNameLoading) {
    return (
      <PageContainer className="bg-background/subtle py-12">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (selectedProspects.length === 0 && !hasAttemptedSubmit) {
    return <Navigate replace to={backLink} />;
  }

  const handleBackToFindProspects = () => {
    navigate(backLink);
  };

  const focusFirstErrorInput = (
    allErrors: Partial<Record<string, ValidationErrors>>,
  ) => {
    if (!tableRef.current) return;

    // Find first prospect with an error (maintaining table order)
    for (const prospect of selectedProspects) {
      const prospectErrors = allErrors[prospect.limitedPartnerId];

      if (!prospectErrors) continue;

      // Check errors in order: min ticket, max ticket
      if (prospectErrors.estimatedTicketSizeMin) {
        const input = tableRef.current.querySelector<HTMLInputElement>(
          `[data-testid="estimatedTicketSizeMin-${prospect.limitedPartnerId}-money-input"]`,
        );

        if (input) {
          input.focus();

          return;
        }
      }

      if (prospectErrors.estimatedTicketSizeMax) {
        const input = tableRef.current.querySelector<HTMLInputElement>(
          `[data-testid="estimatedTicketSizeMax-${prospect.limitedPartnerId}-money-input"]`,
        );

        if (input) {
          input.focus();

          return;
        }
      }
    }
  };

  const handleSubmit = () => {
    // Trigger validation by marking form as submitted
    setHasAttemptedSubmit(true);

    // Compute current ticket size errors for this submit attempt
    const currentTicketSizeErrors: Record<string, ValidationErrors> = {};
    selectedProspects.forEach((prospect) => {
      const min = prospect.estimatedTicketSizeMin?.amount;
      const max = prospect.estimatedTicketSizeMax?.amount;

      if (min !== undefined && max !== undefined && min > max) {
        currentTicketSizeErrors[prospect.limitedPartnerId] = {
          estimatedTicketSizeMin: m.formValidation_estimatedTicketSizeMin_max(),
          estimatedTicketSizeMax: m.formValidation_estimatedTicketSizeMax_min(),
        };
      }
    });

    const hasCurrentTicketSizeErrors =
      Object.keys(currentTicketSizeErrors).length > 0;

    // Block submit if there are ticket size errors
    if (hasCurrentTicketSizeErrors) {
      setTimeout(() => focusFirstErrorInput(currentTicketSizeErrors), 0);

      return;
    }

    // If validation passes, submit
    const shortlistedLimitedPartners = selectedProspects.map((prospect) => ({
      limitedPartnerId: prospect.limitedPartnerId,
      tierLevel: Number(prospect.tierLevel),
      notes: prospect.notes || undefined,
      estimatedTicketSizeMin: prospect.estimatedTicketSizeMin,
      estimatedTicketSizeMax: prospect.estimatedTicketSizeMax,
      reasonForInclusion:
        prospect.reasonForInclusion.length > 0
          ? prospect.reasonForInclusion.join(REASON_FOR_INCLUSION_SEPARATOR)
          : undefined,
    }));

    mutation.mutate({
      dealId,
      data: {
        shortlistedLimitedPartners,
      },
    });
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-12">
        <ThemedIconButton
          dataTestId="back-to-find-prospects-button"
          icon={<BackArrowIcon />}
          onClick={handleBackToFindProspects}
          size="medium"
          type="button"
        />
        <div className="flex-1">
          <CoInvestmentPageHeader
            fundName={dealName}
            isLoading={isDealNameLoading}
            showDrawer={false}
            title={m.coInvestments_addNotesForCommittee()}
          />
        </div>
      </div>

      <div className="mt-6">
        <CoreTable<ProspectWithFormData>
          columns={columns}
          data={selectedProspects}
          dataTestId="add-notes-table"
          getRowId={(row) => row.limitedPartnerId}
          ref={tableRef}
        />

        <div className="mt-8 flex justify-end">
          <ThemedButton
            dataTestId="submit-for-approval-button"
            disabled={mutation.isPending}
            onClick={handleSubmit}
            size="default"
            variant="primary"
          >
            {mutation.isPending
              ? m.common_pending()
              : m.coInvestments_submitForApproval()}
          </ThemedButton>
        </div>
      </div>
    </>
  );
};
